import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, FilePlus2, FileText, Pencil, Plus, RefreshCw, Save, Trash2, Undo2, UploadCloud, X } from 'lucide-react';
import { User } from '../../../../models/auth';
import { callWhatsappChatApi } from '../../../components/WhatsappChat/api';

// Lista de alumnos de prácticas con la que se identifica a quien escribe al WhatsApp de accidentes.
// Se guarda en el CSV del blob users/accidente_practicas/alumnos_practicas.csv solo con las columnas
// nombre;apellido1;apellido2;emailUniversidad;tipo_documento;numero_identificacion;telefono;campus;facultad. Cada «Guardar cambios», sustitución
// o importación sobrescribe ese CSV. Un Excel/CSV que no tenga todas esas columnas se rechaza.

type Alumno = Record<string, string>;

interface AlumnosResponse {
  columnas: string[];
  alumnos: Alumno[];
  version: string;
  origen: string;
  actualizado: string | null;
  actualizado_por: string | null;
  avisos?: string[];
  mensaje?: string;
}

interface AlumnoResumen {
  nombre: string;
  telefono: string;
  emailUniversidad: string;
}

// Respuesta de atencion_practicas_importar_alumnos (simulada o real)
interface ImportResponse extends Partial<AlumnosResponse> {
  simulado: boolean;
  nuevos: AlumnoResumen[];
  ya_estaban: number;
  sin_telefono_ni_correo: AlumnoResumen[];
}

interface AlumnosPracticasEditorProps {
  user: User;
  apiUrl: string;
  apiKey: string;
}

// Columnas que se ven en la tabla; el resto se edita en el formulario de cada alumno
const TABLE_COLUMNS: { key: string; label: string }[] = [
  { key: 'nombre', label: 'Nombre' },
  { key: 'apellido1', label: 'Apellido 1' },
  { key: 'apellido2', label: 'Apellido 2' },
  { key: 'emailUniversidad', label: 'Correo' },
  { key: 'tipo_documento', label: 'Tipo doc.' },
  { key: 'numero_identificacion', label: 'Nº identificación' },
  { key: 'telefono', label: 'Teléfono' },
  { key: 'campus', label: 'Campus' },
  { key: 'facultad', label: 'Facultad' },
];
const REQUIRED_COLUMNS = ['nombre', 'apellido1', 'apellido2', 'emailUniversidad', 'telefono'];
const PAGE_SIZE = 50;

const formatDateCell = (value: string) => (/^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : value);

// La columna volantes guarda los enlaces de los PDF generados separados por " | " (lo rellena el backend)
const enlacesVolantes = (texto: string) =>
  texto
    .split('|')
    .map((enlace) => enlace.trim())
    .filter(Boolean);

// Cada volante se llama <instante UTC>.pdf (AAAAMMDD_HHMMSS_micro), así que la fecha sale del nombre
const fechaVolante = (url: string) => {
  const m = url.match(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})_\d+\.pdf$/);
  if (!m) return '';
  const fecha = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6])));
  return fecha.toLocaleString('es-ES');
};

const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const AlumnosPracticasEditor: React.FC<AlumnosPracticasEditorProps> = ({ user, apiUrl, apiKey }) => {
  const [columns, setColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<Alumno[]>([]);
  const [savedRows, setSavedRows] = useState<Alumno[]>([]);
  const [version, setVersion] = useState('');
  const [info, setInfo] = useState<{ origen: string; actualizado: string | null; actualizado_por: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [editing, setEditing] = useState<{ index: number | null; data: Alumno } | null>(null);
  // Fila (índice en rows) cuyos volantes se están viendo
  const [volantesDe, setVolantesDe] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [importPreview, setImportPreview] = useState<{ fileName: string; contenido: string; data: ImportResponse } | null>(null);

  const callApi = useCallback(
    (accion: string, body: Record<string, unknown> = {}) =>
      callWhatsappChatApi<AlumnosResponse>(apiUrl, apiKey, accion, body),
    [apiUrl, apiKey]
  );

  const applyResponse = (data: AlumnosResponse) => {
    setVolantesDe(null); // los índices pueden cambiar al recargar la lista
    setColumns(data.columnas);
    setRows(data.alumnos);
    setSavedRows(data.alumnos);
    setVersion(data.version);
    setInfo({ origen: data.origen, actualizado: data.actualizado, actualizado_por: data.actualizado_por });
    setWarnings(data.avisos || []);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    try {
      applyResponse(await callApi('atencion_practicas_alumnos'));
    } catch (err: any) {
      setFeedback({ type: 'error', text: err.message || 'No se pudo cargar la lista.' });
    } finally {
      setLoading(false);
    }
  }, [callApi]);

  useEffect(() => {
    load();
  }, [load]);

  const pendingChanges = rows !== savedRows;

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const indexed = rows.map((row, index) => ({ row, index }));
    if (!term) return indexed;
    return indexed.filter(({ row }) =>
      Object.values(row).some((value) => String(value || '').toLowerCase().includes(term))
    );
  }, [rows, search]);

  const formColumns = useMemo(
    // volantes no se edita a mano: lo rellena el sistema y se muestra en su propia ventana
    () => [...REQUIRED_COLUMNS, ...columns.filter((column) => column !== 'volantes' && !REQUIRED_COLUMNS.includes(column))],
    [columns]
  );

  const openNew = () => setEditing({ index: null, data: Object.fromEntries(formColumns.map((column) => [column, ''])) });

  const commitEdit = () => {
    if (!editing) return;
    const data = Object.fromEntries(Object.entries(editing.data).map(([key, value]) => [key, String(value ?? '').trim()]));
    if (!data.nombre || !data.telefono) {
      setFeedback({ type: 'error', text: 'El alumno necesita al menos nombre y teléfono.' });
      return;
    }
    setRows((prev) => (editing.index === null ? [data, ...prev] : prev.map((row, i) => (i === editing.index ? data : row))));
    setEditing(null);
    setFeedback(null);
  };

  const removeRow = (index: number) => {
    const row = rows[index];
    const name = [row.nombre, row.apellido1].filter(Boolean).join(' ') || row.telefono;
    if (!window.confirm(`¿Quitar a ${name} de la lista?`)) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
    setVolantesDe((prev) => (prev === null || prev === index ? null : prev > index ? prev - 1 : prev));
  };

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const data = await callApi('atencion_practicas_guardar_alumnos', {
        alumnos: rows,
        columnas: columns,
        version,
        usuario: user.email,
      });
      applyResponse(data);
      setFeedback({ type: 'success', text: data.mensaje || 'Lista guardada.' });
    } catch (err: any) {
      setFeedback({ type: 'error', text: err.message || 'No se pudo guardar la lista.' });
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (file: File) => {
    if (pendingChanges && !window.confirm('Tienes cambios sin guardar que se perderán. ¿Continuar?')) return;
    if (!window.confirm(`"${file.name}" sustituirá la lista entera de alumnos. ¿Continuar?`)) return;
    setSaving(true);
    setFeedback(null);
    try {
      const data = await callApi('atencion_practicas_subir_alumnos', {
        contenido: await fileToBase64(file),
        version,
        usuario: user.email,
      });
      applyResponse(data);
      setFeedback({ type: 'success', text: data.mensaje || 'Lista sustituida.' });
    } catch (err: any) {
      setFeedback({ type: 'error', text: err.message || 'No se pudo subir el archivo.' });
    } finally {
      setSaving(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Importar solo los alumnos del Excel que no están ya en la lista (por teléfono o correo): primero se
  // simula para enseñar a quién se va a añadir y se guarda al confirmar
  const previewImport = async (file: File) => {
    if (pendingChanges) {
      setFeedback({ type: 'error', text: 'Guarda o descarta los cambios pendientes antes de importar.' });
      if (importInputRef.current) importInputRef.current.value = '';
      return;
    }
    setSaving(true);
    setFeedback(null);
    setImportPreview(null);
    try {
      const contenido = await fileToBase64(file);
      const data = await callWhatsappChatApi<ImportResponse>(apiUrl, apiKey, 'atencion_practicas_importar_alumnos', {
        contenido,
        version,
        simular: true,
      });
      setImportPreview({ fileName: file.name, contenido, data });
    } catch (err: any) {
      setFeedback({ type: 'error', text: err.message || 'No se pudo leer el archivo.' });
    } finally {
      setSaving(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const confirmImport = async () => {
    if (!importPreview) return;
    setSaving(true);
    setFeedback(null);
    try {
      const data = await callWhatsappChatApi<ImportResponse & AlumnosResponse>(
        apiUrl,
        apiKey,
        'atencion_practicas_importar_alumnos',
        { contenido: importPreview.contenido, version, usuario: user.email, simular: false }
      );
      if (data.alumnos) applyResponse(data);
      setImportPreview(null);
      setFeedback({ type: 'success', text: data.mensaje || 'Alumnos importados.' });
    } catch (err: any) {
      setFeedback({ type: 'error', text: err.message || 'No se pudieron importar los alumnos.' });
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    try {
      const data = await callWhatsappChatApi<{ nombre: string; contenido: string }>(
        apiUrl,
        apiKey,
        'atencion_practicas_descargar_alumnos'
      );
      const bytes = Uint8Array.from(atob(data.contenido), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = data.nombre;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setFeedback({ type: 'error', text: err.message || 'No se pudo descargar la lista.' });
    }
  };

  return (
    <div className="ap-schedule ap-students">
      <div className="ap-schedule-footer">
        <div>
          <strong>Alumnos de prácticas</strong>
          <span className="ap-muted">
            {' '}
            · {rows.length} alumnos · sirve para identificar a quien escribe por WhatsApp
            {info && ` · origen: ${info.origen}`}
            {info?.actualizado && ` · actualizada el ${new Date(info.actualizado).toLocaleString('es-ES')}`}
            {info?.actualizado_por && ` por ${info.actualizado_por}`}
          </span>
        </div>
        <div className="ap-header-actions">
          <button type="button" className="ap-btn ap-btn-secondary" onClick={openNew} disabled={loading || saving}>
            <Plus size={16} /> Añadir alumno
          </button>
          <button type="button" className="ap-btn ap-btn-secondary" onClick={() => importInputRef.current?.click()} disabled={loading || saving}>
            <FilePlus2 size={16} /> Importar nuevos (Excel/CSV)
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            hidden
            onChange={(e) => e.target.files?.[0] && previewImport(e.target.files[0])}
          />
          <button type="button" className="ap-btn ap-btn-secondary" onClick={() => fileInputRef.current?.click()} disabled={loading || saving}>
            <UploadCloud size={16} /> Sustituir (Excel/CSV)
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            hidden
            onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
          />
          <button type="button" className="ap-btn ap-btn-secondary" onClick={download} disabled={loading}>
            <Download size={16} /> Descargar CSV
          </button>
        </div>
      </div>

      {feedback && <div className={`ap-feedback ap-feedback-${feedback.type}`}>{feedback.text}</div>}
      {warnings.length > 0 && (
        <div className="ap-feedback ap-feedback-warning">
          {warnings.map((warning) => (
            <div key={warning}>{warning}</div>
          ))}
        </div>
      )}

      {importPreview && (
        <div className="ap-student-form">
          <div className="ap-schedule-footer">
            <strong>Importar desde «{importPreview.fileName}»</strong>
            <button type="button" className="ap-icon-btn" onClick={() => setImportPreview(null)} title="Cancelar">
              <X size={14} />
            </button>
          </div>
          <span>
            <strong>{importPreview.data.nuevos.length}</strong> alumno(s) nuevos ·{' '}
            {importPreview.data.ya_estaban} ya estaban en la lista (mismo teléfono o correo) o repetidos en el archivo
            {importPreview.data.sin_telefono_ni_correo.length > 0 &&
              ` · ${importPreview.data.sin_telefono_ni_correo.length} sin teléfono ni correo (no se importan)`}
          </span>
          {importPreview.data.nuevos.length > 0 && (
            <div className="ap-table-wrap ap-import-list">
              <table className="ap-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Teléfono</th>
                    <th>Correo</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.data.nuevos.map((alumno, index) => (
                    <tr key={index}>
                      <td>{alumno.nombre}</td>
                      <td>{alumno.telefono}</td>
                      <td>{alumno.emailUniversidad}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="ap-header-actions">
            <button
              type="button"
              className="ap-btn"
              onClick={confirmImport}
              disabled={saving || importPreview.data.nuevos.length === 0}
            >
              {saving ? <RefreshCw size={16} className="spinner-icon" /> : <FilePlus2 size={16} />} Importar{' '}
              {importPreview.data.nuevos.length} alumno(s)
            </button>
            <button type="button" className="ap-btn ap-btn-secondary" onClick={() => setImportPreview(null)} disabled={saving}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {pendingChanges && (
        <div className="ap-pending">
          <span>Hay cambios sin guardar en la lista.</span>
          <button type="button" className="ap-btn ap-btn-secondary" onClick={() => setRows(savedRows)} disabled={saving}>
            <Undo2 size={16} /> Descartar
          </button>
          <button type="button" className="ap-btn" onClick={save} disabled={saving}>
            {saving ? <RefreshCw size={16} className="spinner-icon" /> : <Save size={16} />} Guardar cambios
          </button>
        </div>
      )}

      {editing && (
        <div className="ap-student-form">
          <div className="ap-schedule-footer">
            <strong>{editing.index === null ? 'Nuevo alumno' : 'Editar alumno'}</strong>
            <button type="button" className="ap-icon-btn" onClick={() => setEditing(null)} title="Cerrar">
              <X size={14} />
            </button>
          </div>
          <div className="ap-student-grid">
            {formColumns.map((column) => (
              <label key={column} className="ap-field">
                {column}
                {REQUIRED_COLUMNS.includes(column) && ' *'}
                <input
                  className="ap-search"
                  value={editing.data[column] ?? ''}
                  onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [column]: e.target.value } })}
                />
              </label>
            ))}
          </div>
          <div className="ap-header-actions">
            <button type="button" className="ap-btn" onClick={commitEdit}>
              Aplicar
            </button>
            <span className="ap-muted">Los cambios se guardan al pulsar «Guardar cambios».</span>
          </div>
        </div>
      )}

      {volantesDe !== null && rows[volantesDe] && (
        <div className="ap-student-form">
          <div className="ap-schedule-footer">
            <strong>
              Volantes de{' '}
              {[rows[volantesDe].nombre, rows[volantesDe].apellido1, rows[volantesDe].apellido2].filter(Boolean).join(' ') ||
                rows[volantesDe].emailUniversidad}
            </strong>
            <button type="button" className="ap-icon-btn" onClick={() => setVolantesDe(null)} title="Cerrar">
              <X size={14} />
            </button>
          </div>
          {enlacesVolantes(rows[volantesDe].volantes || '').length === 0 && (
            <span className="ap-muted">Este alumno todavía no tiene volantes generados.</span>
          )}
          <ol>
            {enlacesVolantes(rows[volantesDe].volantes || '').map((url) => (
              <li key={url}>
                <a href={url} target="_blank" rel="noreferrer">
                  Abrir volante{fechaVolante(url) ? ` del ${fechaVolante(url)}` : ''}
                </a>
              </li>
            ))}
          </ol>
        </div>
      )}

      <input
        className="ap-search"
        type="search"
        placeholder="Buscar en cualquier columna"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setVisible(PAGE_SIZE);
        }}
      />

      {loading ? (
        <p className="ap-muted">Cargando lista…</p>
      ) : (
        <div className="ap-table-wrap">
          <table className="ap-table">
            <thead>
              <tr>
                {TABLE_COLUMNS.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
                <th>Volantes</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, visible).map(({ row, index }) => (
                <tr key={index} className={!row.telefono ? 'ap-row-warning' : ''}>
                  {TABLE_COLUMNS.map((column) => (
                    <td key={column.key}>{formatDateCell(row[column.key] || '')}</td>
                  ))}
                  <td>
                    {enlacesVolantes(row.volantes || '').length > 0 ? (
                      <button type="button" className="ap-link-btn" onClick={() => setVolantesDe(index)}>
                        <FileText size={14} /> {enlacesVolantes(row.volantes).length}
                      </button>
                    ) : (
                      <span className="ap-muted">0</span>
                    )}
                  </td>
                  <td className="ap-table-actions">
                    <button type="button" className="ap-icon-btn" onClick={() => setEditing({ index, data: { ...row } })} title="Editar">
                      <Pencil size={14} />
                    </button>
                    <button type="button" className="ap-icon-btn" onClick={() => removeRow(index)} title="Quitar">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > visible && (
            <button type="button" className="ap-link-btn ap-more" onClick={() => setVisible((prev) => prev + PAGE_SIZE)}>
              Ver más ({filtered.length - visible} restantes)
            </button>
          )}
          {filtered.length === 0 && <p className="ap-muted ap-empty">No hay alumnos que coincidan.</p>}
        </div>
      )}
    </div>
  );
};

export default AlumnosPracticasEditor;
